export const getCurrencySymbol = (code: string): string => {
  try {
    return (0)
      .toLocaleString('en', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/\d/g, '')
      .trim();
  } catch {
    return code;
  }
};

// en-GB renders USD as `US$12.00`.
const CURRENCY_LOCALES: Record<string, string> = { EUR: 'en-IE', USD: 'en-US' };

export const createCurrencyFormatter = (currencyCode = 'GBP') => {
  const locale = CURRENCY_LOCALES[currencyCode] ?? 'en-GB';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  });
};

/**
 * Well-formedness, **not** truthiness: `Intl.NumberFormat` throws `RangeError` on anything
 * but three ASCII letters, and `CartItem.currencyCode` comes out of the `recoil-persist`
 * cache — so a corrupted entry must not be able to throw out of a formatter and into a
 * click handler.
 *
 * **Deliberately not an ISO 4217 allowlist**, which has been suggested. This pattern is the
 * exact throw boundary: `Intl` rejects `'GB'` and `'GBPP'` but accepts a well-formed
 * unassigned code like `'XYZ'`, rendering it `"XYZ 95.00"`. An allowlist would prevent no
 * crash and would fail the wrong way round — every code here originates in Shopify's
 * `CurrencyCode` enum, so a stale hardcoded list would reject a *valid* currency the day the
 * store opens a new market, silently dropping the currency keys for real customers into
 * events that cannot be deleted.
 */
const CURRENCY_CODE_PATTERN = /^[A-Za-z]{3}$/;

export const isCurrencyCode = (currencyCode?: string): currencyCode is string =>
  !!currencyCode && CURRENCY_CODE_PATTERN.test(currencyCode);

/**
 * A price with its symbol, or `undefined` when the currency is unusable — so callers can
 * **omit the key** rather than write a wrong one into an undeletable analytics event.
 *
 * Shares `createCurrencyFormatter` with `getFormattedCurrency` so an email and the bag
 * cannot disagree on a price, but does not delegate to it: that is a display function and
 * answers `''` for `NaN`, and its `= 'GBP'` default would label an unknown currency as
 * sterling. Never concatenate `getCurrencySymbol` instead — symbol placement is
 * per-currency and this storefront is multi-currency via `@inContext`.
 */
export const formatCurrency = (
  value: number,
  currencyCode?: string,
): string | undefined => {
  if (!isCurrencyCode(currencyCode) || !Number.isFinite(value))
    return undefined;

  return createCurrencyFormatter(currencyCode).format(value);
};

/**
 * Parses a Shopify money string. Numeric rather than a falsiness check, because a product
 * with no compare-at price returns the *truthy* string `"0.0"`.
 *
 * Shared by the cart reducer and both analytics modules so one string cannot be read as two
 * different numbers. Here rather than in any of them because `currency.ts` is vendor-free.
 */
export const toPrice = (value: string | undefined): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** `49.99 * 3` is `149.97000000000003`, and an analytics event keeps whatever it is given. */
export const roundMoney = (value: number): number =>
  Math.round(value * 100) / 100;

const isNumber = (value: string | number): value is number =>
  typeof value === 'number';

/** What a zero price reads as on product cards and cart lines. Checkout totals stay numeric. */
export const FREE_PRICE_LABEL = 'FREE';

/** `getFormattedCurrency`, except an exact zero reads as `FREE_PRICE_LABEL`. NaN still → ''. */
export const formatPriceOrFree = (
  price: string | number,
  currencyCode?: string,
): string => {
  const value = isNumber(price) ? price : parseFloat(price);
  return value === 0
    ? FREE_PRICE_LABEL
    : getFormattedCurrency(price, currencyCode);
};

export const getFormattedCurrency = (
  price: string | number,
  currencyCode?: string,
) => {
  const formatter = createCurrencyFormatter(currencyCode);
  const value = isNumber(price) ? price : parseFloat(price);

  if (isNaN(value)) {
    return '';
  }

  return formatter.format(value);
};
